import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  LoadingPopup,
  B2BSpinner,
  useDeviceTypes,
  TravelSalesEventBanner,
  NotificationBanner,
  BannerSeverity,
  ActionButton,
  Icon,
  IconName,
} from "halifax";
import { useDispatch } from "react-redux";
import {
  CallState,
  MyTripsFilter,
  FlightItineraryState,
  HotelItineraryState,
  VIEWED_MY_TRIPS,
  TRIPS_LOADING_COMPLETE,
  CarReservationState,
  GetExerciseEligibilitiesRequestV1ByItineraryIds,
  GetExerciseEligibilitiesRequestV1Enum,
  HomesReservationState,
  PackageItineraryState,
  ExperienceReservationState,
  getReturnSlice,
  BookedFlightItineraryWithDepartureTime,
  getDepartureSlice,
  HotelItinerary,
  HomesItinerary,
  ItineraryEnum,
  ActivitiesEntryTypeEnum,
} from "redmond";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  isCorpTenant,
  isVesperTenant,
  getConfigTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  useIsTravelApp,
  useQueryParams,
} from "@capone/common";
import { TripsListConnectorProps } from "./container";

import "./styles.scss";
import * as textConstants from "./constants";
import { WatchList } from "./components/WatchList";
import { ItineraryList, TravelCreditsList } from "./components/ItineraryList";
import { NoTripResults } from "./components/NoResults";
import { MobileFlightItineraryDetails } from "./components/ItineraryList/components/MobileFlightItineraryDetails";
import { MobileHotelItineraryDetails } from "./components/ItineraryList/components/MobileHotelItineraryDetails";
import { MobileCarItineraryDetails } from "./components/ItineraryList/components/MobileCarItineraryDetails";
import { DesktopFilterTabs } from "./components/DesktopFilterTabs";
import { MobileFilterDropdown } from "./components/MobileFilterDropdown";
import { ItinerariesModal } from "./components/ItineraryList/components/ItinerariesModal";
import { trackEvent } from "../../api/v1/analytics/trackEvent";
import { MyTripsHeader } from "../common";
import { usePrevious } from "../../hooks/usePrevious";
import { PORTAL_TITLE, MY_TRIPS_TITLE } from "../../lang/textConstants";
import { fetchUserPassengers } from "../../modules/passengers/reducer";
import {
  useExperiments,
  getExperimentVariantCustomVariants,
  ActiveExperiments,
  TRAVEL_SALE_VARIANTS,
  CONTROL,
  TRAVEL_SALE_ACTIVE,
  getExperimentVariant,
  AVAILABLE,
  VARIANT_B,
  XSELL_ACTIVITIES_MYTRIPS_EXPERIMENT_VARIANTS,
  VARIANT_C,
} from "../../context/experiments";
import { PATH_TRAVEL_SALE } from "../../utils/paths";
import { buildExploreActivitiesActions } from "../../utils/activitiesHelpers";
import { buildActivitiesXsellSearchLocation } from "./utils/buildActivitiesXsellSearchLocation";
import { MobileHomeItineraryDetails } from "./components/ItineraryList/components/MobileHomeItineraryDetails";
import { config } from "../../api/config";
import { TripsTabTitle } from "./components/ItineraryList/components/TripsTabTitle";
import { MobilePackageHotelDetails } from "./components/ItineraryList/components/MobilePackageHotelDetails";
import { MobilePackageFlightDetails } from "./components/ItineraryList/components/MobilePackageFlightDetails";
import { MobileExperienceReservationDetails } from "./components/ItineraryList/components/MobileExperienceReservationDetails";
import {
  FLIGHT_DATES_SESSION_STORAGE_KEY,
  FLIGHT_ROUTE_SESSION_STORAGE_KEY,
} from "../../features/change-for-any-reason/constants";
import { TravelAppPage } from "./components/ItineraryList/components/TravelAppPage";
import { MyTripsCsatSurveyModal } from "./components/MyTripsCsatSurveyModal";
import { useMyTripsCsatSurvey } from "./hooks/useMyTripsCsatSurvey";
import { ActivitiesXsellSection } from "./components/ActivitiesXsellSection";
import { TripsReplatformNoticeBanner } from "./components/TripsReplatformNoticeBanner";
import { ClientContext } from "../../App";
import {
  getIsTravelAppModalAction,
  shouldFetchItineraryCategory,
} from "./utils/travelAppTripDetails";

export interface ITripsListProps
  extends TripsListConnectorProps,
  RouteComponentProps { }

export const TripsList = ({
  fetchCars,
  fetchFlights,
  fetchHotels,
  fetchFlightDisruptions,
  history,
  listWatches,
  selectedFlight,
  selectedHotel,
  selectedCar,
  tripsFilter,
  areTripsLoading,
  hasError,
  hasTripsToDisplay,
  openModal,
  activeCfarCount,
  partialFlightsListDpExerciseFactsProperties,
  partialEligibilityDpExerciseFactsProperties,
  fetchFlightDisruptionsCallState,
  tripsCounts,
  dpAttachedFlightItineraryIds,
  fetchHomes,
  selectedHome,
  listPaymentMethods,
  setTripSearchQuery,
  fetchPackages,
  selectedPackageHotel,
  selectedPackageFlight,
  fetchExperiences,
  fetchRedeemedEarnOffers,
  selectedExperience,
  populateTripQueryParams,
  setSelectedFlight,
  setSelectedHotel,
  setSelectedCar,
  setSelectedHome,
  setSelectedPackage,
  setSelectedPackageFlight,
  setSelectedPackageHotel,
  setSelectedExperience,
  selectedPackage,
  nextUpcomingTrip,
  airportMap,
  activitiesXsellExperiences,
  activitiesXsellCallState,
  fetchActivitiesXsell,
  activitiesXsellRewardsAccount,
  fetchRewardsAccounts,
}: ITripsListProps) => {
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const { isConsumerMultiPortal } = useContext(ClientContext);

  const expState = useExperiments();

  const [fetchAll, setFetchAll] = useState(true);
  const [hasFetchedPast, setHasFetchedPast] = useState(false);
  const [hasFetchedCanceled, setHasFetchedCanceled] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [isActivitiesBannerDismissed, setIsActivitiesBannerDismissed] =
    useState(false);

  const nextUpcomingTripBannerInfo = useMemo(() => {
    if (!nextUpcomingTrip) return null;
    if (nextUpcomingTrip.type === ItineraryEnum.Flight) {
      const flight = nextUpcomingTrip as BookedFlightItineraryWithDepartureTime;
      const departureSlice = getDepartureSlice(flight.bookedItinerary);
      const lastDepSegment =
        departureSlice.segments[departureSlice.segments.length - 1];
      const returnSlice = getReturnSlice(flight.bookedItinerary);
      const locationCode = lastDepSegment?.destination.locationCode;
      const airport = locationCode ? airportMap[locationCode] : undefined;
      const fromDate = flight.departureTime ?? null;
      const untilDate = returnSlice?.segments[0].scheduledDeparture ?? fromDate;

      return {
        location: airport?.cityName ?? locationCode ?? null,
        searchLocation: null,
        coords: airport?.coordinates ?? null,
        fromDate,
        untilDate,
        isOngoing: fromDate ? !dayjs().isBefore(dayjs(fromDate)) : false,
      };
    }
    if (nextUpcomingTrip.type === ItineraryEnum.Hotel) {
      const hotel = nextUpcomingTrip as HotelItinerary;
      const { city, country } = hotel.reservation.lodgingData;
      return {
        location: city ?? null,
        searchLocation: buildActivitiesXsellSearchLocation(city, country),
        coords: hotel.reservation.lodgingData?.location?.coordinates ?? null,
        fromDate: hotel.reservation.checkInDate,
        untilDate: hotel.reservation.checkOutDate,
        isOngoing: !dayjs().isBefore(dayjs(hotel.reservation.checkInDate)),
      };
    }
    if (nextUpcomingTrip.type === ItineraryEnum.Home) {
      const home = nextUpcomingTrip as HomesItinerary;
      const homeLocation =
        home.reservation.listing.market?.name ??
        home.reservation.listing.areaName ??
        null;
      return {
        location: homeLocation,
        searchLocation: null,
        coords: home.reservation.listing.location?.coordinates ?? null,
        fromDate: home.reservation.stayDates.from,
        untilDate: home.reservation.stayDates.until,
        isOngoing: !dayjs().isBefore(dayjs(home.reservation.stayDates.from)),
      };
    }
    return null;
  }, [nextUpcomingTrip, airportMap]);

  useEffect(() => {
    if (
      !areTripsLoading &&
      nextUpcomingTripBannerInfo?.location &&
      activitiesXsellCallState === CallState.NotCalled
    ) {
      fetchActivitiesXsell(
        nextUpcomingTripBannerInfo.searchLocation ??
          nextUpcomingTripBannerInfo.location,
        nextUpcomingTripBannerInfo.coords ?? undefined,
        nextUpcomingTripBannerInfo.fromDate,
        nextUpcomingTripBannerInfo.untilDate,
      );
    }
  }, [areTripsLoading, nextUpcomingTripBannerInfo]);

  const params = useQueryParams();

  const { isTravelApp } = useIsTravelApp();

  const { tripId, itineraryType, action } = params;
  // Only itineraryType/action combos with a dedicated modal in TravelAppPage
  // route there; everything else (no action, or an unsupported action for
  // that itineraryType) uses the normal view+modal flow so its modals
  // (traveler info, payment, resend confirmation, etc.) work.
  const isTravelAppTripDetailsPage =
    isTravelApp &&
    !!tripId &&
    !!itineraryType &&
    getIsTravelAppModalAction(itineraryType, action);

  const isPostBookingOfferMyTripsEnabled = useMemo(
    () =>
      getExperimentVariant(
        expState.experiments,
        ActiveExperiments.POST_BOOKING_OFFER_MY_TRIPS,
      ) === AVAILABLE,
    [expState.experiments],
  );

  const xsellActivitiesMyTripsExperiment = getExperimentVariantCustomVariants(
    expState.experiments,
    ActiveExperiments.XSELL_ACTIVITIES_MYTRIPS_EXPERIMENT,
    XSELL_ACTIVITIES_MYTRIPS_EXPERIMENT_VARIANTS,
  );
  const isXsellActivitiesMyTripsExperimentInVariantB = useMemo(
    () => xsellActivitiesMyTripsExperiment === VARIANT_B,
    [xsellActivitiesMyTripsExperiment],
  );
  const isXsellActivitiesMyTripsExperimentInVariantC = useMemo(
    () => xsellActivitiesMyTripsExperiment === VARIANT_C,
    [xsellActivitiesMyTripsExperiment],
  );

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    ActiveExperiments.TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const isXmodRedesign =
    getExperimentVariant(
      expState.experiments,
      ActiveExperiments.XMOD_V1_EXPERIMENT,
    ) === AVAILABLE;

  useEffect(() => {
    document.title = MY_TRIPS_TITLE;
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchUserPassengers());
    fetchRewardsAccounts();
    trackEvent({
      eventName: VIEWED_MY_TRIPS,
      properties: {
        entry_point: params.entry_point,
      },
    });
  }, []);

  // On mount, clear any stale Redux selection so the fetch sagas re-select
  // based on the current tripId in the URL.
  useEffect(() => {
    setSelectedFlight(null);
    setSelectedHotel(null);
    setSelectedCar(null);
    setSelectedHome(null);
    setSelectedPackage(null);
    setSelectedPackageFlight(null);
    setSelectedPackageHotel(null);
    setSelectedExperience(null);
  }, []);

  // handle browser-based history navigation in travel app
  useEffect(() => {
    if (isTravelApp) {
      if (params.tripId && params.itineraryType) {
        return;
      }
      if (params.tripId) {
        populateTripQueryParams(history);
      }
    }

    if (!params.tripId) {
      setSelectedFlight(null);
      setSelectedHotel(null);
      setSelectedCar(null);
      setSelectedHome(null);
      setSelectedPackage(null);
      setSelectedPackageFlight(null);
      setSelectedPackageHotel(null);
      setSelectedExperience(null);
    }
  }, [isTravelApp, params]);

  // Reset ChFAR Search Session Storage
  useEffect(() => {
    sessionStorage.removeItem(FLIGHT_DATES_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(FLIGHT_ROUTE_SESSION_STORAGE_KEY);
  }, []);

  const fetchUpcomingItineraries = () => {
    if (
      shouldFetchItineraryCategory("car", isTravelApp, itineraryType, action)
    ) {
      fetchCars(
        {
          states: [CarReservationState.Present, CarReservationState.Future],
          asOf: new Date().toISOString(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "flight",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchFlights(
        {
          states: [FlightItineraryState.Present, FlightItineraryState.Future],
          referenceDateTime: dayjs().format(),
        },
        history,
        isPostBookingOfferMyTripsEnabled,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "hotel",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchHotels(
        {
          states: [HotelItineraryState.Present, HotelItineraryState.Future],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory("home", isTravelApp, itineraryType, action)
    ) {
      fetchHomes(
        {
          states: [HomesReservationState.Present, HomesReservationState.Future],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "package",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchPackages(
        {
          states: [PackageItineraryState.Present, PackageItineraryState.Future],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    fetchRedeemedEarnOffers();
    if (
      shouldFetchItineraryCategory(
        "activity",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchExperiences(
        {
          states: [
            ExperienceReservationState.Present,
            ExperienceReservationState.Future,
          ],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
  };

  const fetchCanceledItineraries = () => {
    if (
      shouldFetchItineraryCategory("car", isTravelApp, itineraryType, action)
    ) {
      fetchCars(
        {
          states: [CarReservationState.Canceled],
          asOf: new Date().toISOString(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "flight",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchFlights(
        {
          states: [FlightItineraryState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "hotel",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchHotels(
        {
          states: [HotelItineraryState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history,
        [HotelItineraryState.Canceled],
      );
    }
    if (
      shouldFetchItineraryCategory("home", isTravelApp, itineraryType, action)
    ) {
      fetchHomes(
        {
          states: [HomesReservationState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "package",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchPackages(
        {
          states: [PackageItineraryState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "activity",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchExperiences(
        {
          states: [ExperienceReservationState.Canceled],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
  };

  const fetchPastItineraries = () => {
    if (
      shouldFetchItineraryCategory("car", isTravelApp, itineraryType, action)
    ) {
      fetchCars(
        {
          states: [CarReservationState.Past],
          asOf: new Date().toISOString(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "flight",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchFlights(
        {
          states: [FlightItineraryState.Past],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "hotel",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchHotels(
        {
          states: [HotelItineraryState.Past],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory("home", isTravelApp, itineraryType, action)
    ) {
      fetchHomes(
        {
          states: [HomesReservationState.Past],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "package",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchPackages(
        {
          states: [PackageItineraryState.Past],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
    if (
      shouldFetchItineraryCategory(
        "activity",
        isTravelApp,
        itineraryType,
        action,
      )
    ) {
      fetchExperiences(
        {
          states: [ExperienceReservationState.Past],
          referenceDateTime: dayjs().format(),
        },
        history,
      );
    }
  };

  useEffect(() => {
    if (!fetchAll || isTravelAppTripDetailsPage) return;
    if (![CallState.Success, CallState.Failed].includes(expState.callState))
      return;
    setFetchAll(false);
    fetchUpcomingItineraries();
    listWatches();
    listPaymentMethods();

    // If the user lands on a past/canceled/credits tab (e.g., via URL query param),
    // or has a tripId that might be in past/canceled trips, fetch immediately.
    if (tripId) {
      setHasFetchedPast(true);
      setHasFetchedCanceled(true);
      fetchPastItineraries();
      fetchCanceledItineraries();
    } else if (tripsFilter === MyTripsFilter.CANCELED_TRIPS) {
      setHasFetchedCanceled(true);
      fetchCanceledItineraries();
    } else if (
      tripsFilter === MyTripsFilter.PAST_TRIPS ||
      tripsFilter === MyTripsFilter.TRAVEL_CREDITS
    ) {
      setHasFetchedPast(true);
      fetchPastItineraries();
    }
  }, [expState.callState, fetchAll]);

  useEffect(() => {
    if (!hasFetchedCanceled && tripsFilter === MyTripsFilter.CANCELED_TRIPS) {
      setHasFetchedCanceled(true);
      fetchCanceledItineraries();
    }
    if (
      !hasFetchedPast &&
      (tripsFilter === MyTripsFilter.PAST_TRIPS ||
        tripsFilter === MyTripsFilter.TRAVEL_CREDITS)
    ) {
      setHasFetchedPast(true);
      fetchPastItineraries();
    }
  }, [tripsFilter, hasFetchedPast, hasFetchedCanceled]);

  useEffect(() => {
    if (dpAttachedFlightItineraryIds.length > 0) {
      fetchFlightDisruptions({
        itineraryIds: dpAttachedFlightItineraryIds,
        GetExerciseEligibilitiesRequestV1:
          GetExerciseEligibilitiesRequestV1Enum.ByItineraryIds,
      } as GetExerciseEligibilitiesRequestV1ByItineraryIds);
    }
    // JSON.stringify is needed to only compare the array content, not reference
    // since the array reference is "different" with every creation.
    // The operation should be quick given array's small size.
  }, [JSON.stringify(dpAttachedFlightItineraryIds)]);

  const previousAreTripsLoading = usePrevious(areTripsLoading);

  useEffect(() => {
    // Fire an event with certain trip info ONLY WHEN all the trips loading is complete.
    // Don't fire pageLoaded if we're in TravelAppPage (to prevent double firing)
    if (
      previousAreTripsLoading &&
      !areTripsLoading &&
      fetchFlightDisruptionsCallState !== CallState.InProcess &&
      !isTravelAppTripDetailsPage
    ) {
      trackEvent({
        eventName: TRIPS_LOADING_COMPLETE,
        properties: {
          ...partialFlightsListDpExerciseFactsProperties,
          ...partialEligibilityDpExerciseFactsProperties,
          cfar_active: activeCfarCount,
        },
      });

      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.PAGE_LOADED,
      });
    }
  }, [
    areTripsLoading,
    fetchFlightDisruptionsCallState,
    isTravelAppTripDetailsPage,
  ]);

  const totalTripsCount =
    (tripsCounts[MyTripsFilter.UPCOMING_TRIPS] ?? 0) +
    (tripsCounts[MyTripsFilter.PAST_TRIPS] ?? 0) +
    (tripsCounts[MyTripsFilter.CANCELED_TRIPS] ?? 0);

  const hasBookedTrip = totalTripsCount > 0;

  const myTripsCsatSurvey = useMyTripsCsatSurvey({
    areTripsLoading,
    experiments: expState.experiments,
    hasBookedTrip,
    hasOpenTripsModal: Boolean(openModal?.type),
    isMobile: matchesMobile,
    isTravelAppTripDetailsPage,
    path: history.location.pathname,
    totalTripsCount,
  });

  const tabTitles = textConstants.getTabTitles(tripsFilter, tripsCounts);

  const renderFilteredList = () => {
    switch (tripsFilter) {
      case MyTripsFilter.UPCOMING_TRIPS:
      case MyTripsFilter.CANCELED_TRIPS:
      case MyTripsFilter.PAST_TRIPS:
      case MyTripsFilter.TRIPS_WITH_OFFERS:
        return (
          <ItineraryList
            isMobile={matchesMobile}
            showDisruptionProtectionElements
          />
        );
      case MyTripsFilter.WATCHED_TRIPS:
        return <WatchList isMobile={matchesMobile} />;
      case MyTripsFilter.TRAVEL_CREDITS:
        return <TravelCreditsList isMobile={matchesMobile} />;
      default:
        return null;
    }
  };

  if (isTravelAppTripDetailsPage) {
    return <TravelAppPage />;
  }

  if (
    matchesMobile &&
    (selectedFlight ||
      selectedHotel ||
      selectedCar ||
      selectedHome ||
      selectedPackage ||
      selectedPackageHotel ||
      selectedPackageFlight ||
      selectedExperience)
  ) {
    return (
      <>
        {selectedFlight && (
          <MobileFlightItineraryDetails showDisruptionProtectionElements />
        )}
        {selectedHotel && <MobileHotelItineraryDetails />}
        {selectedCar && <MobileCarItineraryDetails />}
        {selectedHome && <MobileHomeItineraryDetails />}
        {selectedPackageHotel && <MobilePackageHotelDetails />}
        {selectedPackageFlight && <MobilePackageFlightDetails />}
        {selectedExperience && <MobileExperienceReservationDetails />}

        <ItinerariesModal isMobile={matchesMobile} />
      </>
    );
  }

  return (
    <Box
      className={clsx({ mobile: matchesMobile }, "trips-list", config.TENANT)}
    >
      {!isTravelApp && <MyTripsHeader history={history} useSecondaryHeader />}
      {!isTravelApp && (
        <TripsReplatformNoticeBanner
          isConsumerMultiPortal={!!isConsumerMultiPortal}
        />
      )}
      {matchesMobile ? (
        !isTravelApp && <MobileFilterDropdown isXmodRedesign={isXmodRedesign} />
      ) : (
        <DesktopFilterTabs />
      )}
      {matchesMobile && travelSaleVariant !== CONTROL && (
        <TravelSalesEventBanner
          variant={
            travelSaleVariant === TRAVEL_SALE_ACTIVE ? "default" : "with-timer"
          }
          onClick={() => {
            history.push(`${PATH_TRAVEL_SALE}?entryType=my_trips`);
          }}
          subtitle={
            travelSaleVariant === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_SUBTITLE
          }
          buttonText={
            travelSaleVariant! === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_CTA
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_CTA
          }
          isMobile
        />
      )}
      <div
        id={`${tripsFilter}-tab`}
        role="tabpanel"
        aria-labelledby={tripsFilter}
        className={clsx("tab-content", {
          redesigned: isVesperTenant(getConfigTenant()),
        })}
      >
        {areTripsLoading && (
          <LoadingPopup
            open={areTripsLoading || areTripsLoading === null}
            indicator={B2BSpinner}
            message={textConstants.LOADING_TRIPS}
            loadingIndicatorTextVariant={matchesMobile ? undefined : "h2"}
            classes={[
              "trips-loading-modal",
              ...(matchesMobile ? ["mobile"] : []),
            ]}
          />
        )}
        {(isXmodRedesign
          ? !matchesMobile
          : hasTripsToDisplay || isCorpTenant(config.TENANT)) && (
            <TripsTabTitle
              localSearchQuery={localSearchQuery}
              setLocalSearchQuery={setLocalSearchQuery}
              setTripSearchQuery={setTripSearchQuery}
              isMobile={matchesMobile}
              isXmodRedesign={isXmodRedesign}
              title={tabTitles?.title ?? ""}
              subTitle={tabTitles?.subTitle ?? ""}
            />
          )}
        {isXsellActivitiesMyTripsExperimentInVariantC &&
          !areTripsLoading &&
          !!nextUpcomingTripBannerInfo?.location && tripsFilter === MyTripsFilter.UPCOMING_TRIPS && !isTravelApp && (
            <ActivitiesXsellSection
              location={nextUpcomingTripBannerInfo.location}
              experiences={activitiesXsellExperiences}
              callState={activitiesXsellCallState}
              rewardsAccount={activitiesXsellRewardsAccount}
              onCtaClick={
                buildExploreActivitiesActions({
                  cityLabel: nextUpcomingTripBannerInfo.location,
                  fallbackLabel: nextUpcomingTripBannerInfo.location,
                  coords: nextUpcomingTripBannerInfo.coords ?? undefined,
                  fromDate: nextUpcomingTripBannerInfo.fromDate,
                  untilDate: nextUpcomingTripBannerInfo.untilDate,
                  isTravelApp,
                  history,
                  product: nextUpcomingTrip.type,
                })[0].onClick
              }
              matchesMobile={matchesMobile}
              history={history}
              fromDate={nextUpcomingTripBannerInfo.fromDate}
              untilDate={nextUpcomingTripBannerInfo.untilDate}
              isTravelApp={isTravelApp}
              entryType={
                nextUpcomingTrip.type === ItineraryEnum.Flight
                  ? ActivitiesEntryTypeEnum.MYTRIPS_XSELL_FLIGHT
                  : nextUpcomingTrip.type === ItineraryEnum.Hotel ||
                    nextUpcomingTrip.type === ItineraryEnum.Home
                    ? ActivitiesEntryTypeEnum.MYTRIPS_XSELL_STAY
                    : nextUpcomingTrip.type === ItineraryEnum.Experience
                      ? ActivitiesEntryTypeEnum.MYTRIPS_XSELL_ACTIVITY
                      : ActivitiesEntryTypeEnum.MYTRIPS_XSELL
              }
            />
          )}
        {isXsellActivitiesMyTripsExperimentInVariantB &&
          !isActivitiesBannerDismissed &&
          !areTripsLoading &&
          !!nextUpcomingTripBannerInfo?.location && (
            <NotificationBanner
              severity={BannerSeverity.NOTICE}
              onDismiss={() => setIsActivitiesBannerDismissed(true)}
              content={
                <Box className="activities-cross-sell-banner-content">
                  <Icon name={IconName.ExperiencesIconCircle} />
                  <Box className="activities-cross-sell-banner-text-and-cta">
                    <Box className="activities-cross-sell-banner-text">
                      <Typography
                        variant="h3"
                        className="activities-cross-sell-banner-title"
                      >
                        {nextUpcomingTripBannerInfo.isOngoing
                          ? textConstants.getActivitiesCrossSellOngoingBannerTitle(
                            nextUpcomingTripBannerInfo.location,
                          )
                          : textConstants.getActivitiesCrossSellBannerTitle(
                            nextUpcomingTripBannerInfo.location,
                          )}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        className="activities-cross-sell-banner-subtitle"
                      >
                        {nextUpcomingTripBannerInfo.isOngoing
                          ? textConstants.ACTIVITIES_CROSS_SELL_ONGOING_BANNER_SUBTITLE
                          : textConstants.ACTIVITIES_CROSS_SELL_BANNER_SUBTITLE}
                      </Typography>
                    </Box>
                    <ActionButton
                      className="activities-cross-sell-banner-cta b2b"
                      message={textConstants.ACTIVITIES_CROSS_SELL_BANNER_CTA}
                      onClick={
                        buildExploreActivitiesActions({
                          cityLabel: nextUpcomingTripBannerInfo.location,
                          fallbackLabel: nextUpcomingTripBannerInfo.location,
                          coords:
                            nextUpcomingTripBannerInfo.coords ?? undefined,
                          fromDate: nextUpcomingTripBannerInfo.fromDate,
                          untilDate: nextUpcomingTripBannerInfo.untilDate,
                          isTravelApp,
                          history,
                          product: nextUpcomingTrip.type,
                        })[0].onClick
                      }
                    />
                  </Box>
                </Box>
              }
            />
          )}
        {!areTripsLoading && (
          <NoTripResults
            isMobile={matchesMobile}
            hasFilterSearchQuery={localSearchQuery.length > 0}
            clearFilterSearchQuery={() => {
              setLocalSearchQuery("");
              setTripSearchQuery("");
            }}
          />
        )}
        {(hasTripsToDisplay || (!hasError && openModal)) &&
          renderFilteredList()}
      </div>
      <MyTripsCsatSurveyModal
        isOpen={myTripsCsatSurvey.isOpen}
        onComplete={myTripsCsatSurvey.onComplete}
        onDismiss={myTripsCsatSurvey.onDismiss}
        onResponse={myTripsCsatSurvey.onResponse}
      />
    </Box>
  );
};
